import React, { useEffect, useMemo, useState } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Container,
  Spinner,
  Table,
} from "reactstrap"
import Select from "react-select"
import api from "../../services/api"
import AuthService from "../../services/Auth.service"
import Swal from "sweetalert2"
import moment from "moment"
import {
  getConsumoPeriodoTotal,
  getLeituraAtualDaLinha,
} from "../Consumo/consumoCalculations"

const GRANULARIDADE_OPTIONS = [
  { value: "diario", label: "Linhas por dia" },
  { value: "pulso", label: "Por pulso" },
]

const REGISTRO_FETCH_LIMIT = 50000

const formatDateBr = value => {
  if (!value) return "—"
  if (typeof value === "string") {
    const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`
  }
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })
}

const formatDateTimeBr = value => {
  if (!value) return "—"
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })
}

const formatNumber = (value, digits = 3) => {
  const n = Number(value)
  if (!Number.isFinite(n)) {
    return (0).toLocaleString("pt-BR", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })
  }
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

const toDiaKey = dt => {
  const d = new Date(dt)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" })
}

const round3 = n => Math.round(Number(n) * 1000) / 1000

/** Normaliza leituras do registro e monta linhas diário/pulso (estilo RelatorioConsumo). */
const buildRelatorioFromRegistros = (
  registros,
  { granularidade, excluirInvalidos, periodo },
) => {
  const leituras = (Array.isArray(registros) ? registros : [])
    .map(row => {
      const leituraAtual = Number(
        row.NR_LEITURA_ATUAL ?? getLeituraAtualDaLinha(row),
      )
      const oidMedidor = Number(
        row.OID_MEDIDOR ?? row.oid_medidor ?? row.OID_HIDROMETRO,
      )
      return {
        OID_MEDIDOR: Number.isFinite(oidMedidor) ? oidMedidor : 0,
        NR_HIDROMETRO:
          row.NR_HIDROMETRO_IMOVEL ||
          row.NR_HIDROMETRO ||
          row.NR_MEDIDOR ||
          "",
        DS_COMPLEMENTO:
          row.DS_COMPLEMENTO_IMOVEL || row.DS_COMPLEMENTO || row.imovel || "",
        DS_BLOCO: row.DS_BLOCO_IMOVEL || row.DS_BLOCO || "",
        DT_LEITURA: row.DT_LEITURA,
        NR_PULSO: Number(row.NR_PULSO || 0),
        VR_LITROS_POR_PULSO: Number(row.VR_LITROS_POR_PULSO || 0),
        LEITURA_ATUAL: Number.isFinite(leituraAtual) ? leituraAtual : 0,
        _raw: row,
      }
    })
    .filter(r => r.DT_LEITURA)
    .sort((a, b) => {
      if (a.OID_MEDIDOR !== b.OID_MEDIDOR) {
        return a.OID_MEDIDOR - b.OID_MEDIDOR
      }
      return new Date(a.DT_LEITURA).getTime() - new Date(b.DT_LEITURA).getTime()
    })

  const deltaLitros = (curr, prev) => {
    if (prev == null || !Number.isFinite(prev)) return 0
    const raw = curr - prev
    return excluirInvalidos ? Math.max(0, raw) : raw
  }

  const byMeter = new Map()
  for (const row of leituras) {
    const key = row.OID_MEDIDOR || row.NR_HIDROMETRO
    const cur = byMeter.get(key)
    if (!cur) {
      byMeter.set(key, {
        first: row.LEITURA_ATUAL,
        last: row.LEITURA_ATUAL,
        hidrometro: row.NR_HIDROMETRO,
      })
    } else {
      cur.last = row.LEITURA_ATUAL
    }
  }

  let totalLitros = 0
  for (const m of byMeter.values()) {
    const c = excluirInvalidos
      ? Math.max(0, m.last - m.first)
      : m.last - m.first
    totalLitros += c
  }

  let itens = []
  if (granularidade === "pulso") {
    const prevByMeter = new Map()
    itens = leituras.map(row => {
      const meterKey = row.OID_MEDIDOR || row.NR_HIDROMETRO
      const prev = prevByMeter.has(meterKey) ? prevByMeter.get(meterKey) : null
      const consumo = deltaLitros(row.LEITURA_ATUAL, prev)
      prevByMeter.set(meterKey, row.LEITURA_ATUAL)
      return {
        OID_MEDIDOR: row.OID_MEDIDOR,
        NR_HIDROMETRO: row.NR_HIDROMETRO,
        DS_COMPLEMENTO: row.DS_COMPLEMENTO,
        DS_BLOCO: row.DS_BLOCO,
        DT_LEITURA: row.DT_LEITURA,
        NR_PULSO: row.NR_PULSO,
        LEITURA_ATUAL: row.LEITURA_ATUAL,
        CONSUMO_LITROS: consumo,
        CONSUMO_M3: round3(consumo / 1000),
      }
    })
    itens.reverse()
  } else {
    const days = new Map()
    for (const row of leituras) {
      const dia = toDiaKey(row.DT_LEITURA)
      const key = `${row.OID_MEDIDOR || row.NR_HIDROMETRO}|${dia}`
      const cur = days.get(key)
      if (!cur) {
        days.set(key, {
          OID_MEDIDOR: row.OID_MEDIDOR,
          NR_HIDROMETRO: row.NR_HIDROMETRO,
          DS_COMPLEMENTO: row.DS_COMPLEMENTO,
          DS_BLOCO: row.DS_BLOCO,
          DT_DIA: dia,
          LEITURA_INI: row.LEITURA_ATUAL,
          LEITURA_FIM: row.LEITURA_ATUAL,
          DT_PRIMEIRA: row.DT_LEITURA,
          QTD_LEITURAS: 1,
        })
      } else {
        cur.LEITURA_FIM = row.LEITURA_ATUAL
        cur.QTD_LEITURAS += 1
      }
    }
    itens = Array.from(days.values())
      .map(d => {
        const consumo = excluirInvalidos
          ? Math.max(0, d.LEITURA_FIM - d.LEITURA_INI)
          : d.LEITURA_FIM - d.LEITURA_INI
        return {
          ...d,
          CONSUMO_LITROS: consumo,
          CONSUMO_M3: round3(consumo / 1000),
        }
      })
      .sort((a, b) => {
        if (a.DT_DIA !== b.DT_DIA) return a.DT_DIA < b.DT_DIA ? 1 : -1
        return (a.NR_HIDROMETRO || "").localeCompare(b.NR_HIDROMETRO || "")
      })
  }

  const latestReading =
    leituras.length > 0
      ? Math.max(...leituras.map(r => r.LEITURA_ATUAL))
      : null

  // Fallback de total com a mesma fórmula do Consumo (quando há campos de registro)
  const totalFromCalc = getConsumoPeriodoTotal(
    leituras.map(r => r._raw || r),
  )
  const totalLitrosFinal =
    totalLitros > 0 ? totalLitros : Math.max(0, totalFromCalc)

  return {
    periodo,
    opcoes: {
      excluir_invalidos: excluirInvalidos,
      granularidade,
    },
    qtd_medidores: byMeter.size,
    qtd_linhas: itens.length,
    total_litros: round3(totalLitrosFinal),
    total_m3: round3(totalLitrosFinal / 1000),
    latestReading,
    itens,
  }
}

async function fetchRegistroMedidor(oidMedidor, startDate, endDate) {
  const { data } = await api.post(
    "/consumptions/registro",
    {
      OID_MEDIDOR: oidMedidor,
      OID_CONDOMINIO: "",
      DT_FILTER_INI: startDate,
      DT_FILTER_FIM: endDate,
      limit: REGISTRO_FETCH_LIMIT,
      offset: 0,
    },
    { timeout: 120000 },
  )
  const rows = Array.isArray(data) ? data : data?.data || []
  // Garante OID_MEDIDOR nas linhas (API pode omitir) — só os da unidade.
  return rows.map(row => ({
    ...row,
    OID_MEDIDOR: row.OID_MEDIDOR ?? oidMedidor,
  }))
}

const MoradorDashboard = () => {
  const user = AuthService.getCurrentUser()
  const oidUsuario = user?.user?.OID_USUARIO

  const [meters, setMeters] = useState([])
  const [units, setUnits] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [startDate, setStartDate] = useState(
    moment().startOf("month").format("YYYY-MM-DD"),
  )
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"))
  const [granularidade, setGranularidade] = useState(GRANULARIDADE_OPTIONS[0])
  const [excluirInvalidos, setExcluirInvalidos] = useState(true)
  const [relatorio, setRelatorio] = useState(null)

  const ownedMeterIds = useMemo(
    () =>
      meters
        .map(m => Number(m.OID_MEDIDOR))
        .filter(id => Number.isFinite(id) && id > 0),
    [meters],
  )

  useEffect(() => {
    if (!oidUsuario) return undefined

    let cancelled = false
    const load = async () => {
      try {
        const [hidroRes, imoveisRes] = await Promise.all([
          api.get(`/imovel/hidro/${oidUsuario}`),
          api.get(`/imovel/user/${oidUsuario}`).catch(() => ({ data: [] })),
        ])
        if (cancelled) return
        setMeters(Array.isArray(hidroRes.data) ? hidroRes.data : [])
        setUnits(Array.isArray(imoveisRes.data) ? imoveisRes.data : [])
      } catch (err) {
        console.warn("[MoradorDashboard] falha ao carregar unidade", err)
        if (!cancelled) {
          setMeters([])
          setUnits([])
        }
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [oidUsuario])

  const unitLabel =
    units[0]?.imovel ||
    meters[0]?.imovel ||
    (units[0]?.IM_COMPLEMENTO
      ? `AP/CASA: ${units[0].IM_COMPLEMENTO}`
      : "Unidade vinculada")

  const isPulso = relatorio?.opcoes?.granularidade === "pulso"

  const gerarRelatorio = async () => {
    if (!ownedMeterIds.length) {
      Swal.fire({
        icon: "info",
        text: "Nenhum medidor vinculado à sua unidade.",
      })
      return
    }
    if (!startDate || !endDate) {
      Swal.fire({
        icon: "warning",
        text: "Informe a data início e a data fim.",
      })
      return
    }
    if (startDate > endDate) {
      Swal.fire({
        icon: "warning",
        text: "A data fim não pode ser menor que a data início.",
      })
      return
    }

    setIsLoading(true)
    setRelatorio(null)
    try {
      const batches = await Promise.all(
        ownedMeterIds.map(id => fetchRegistroMedidor(id, startDate, endDate)),
      )
      const allRows = batches.flat()

      if (!allRows.length) {
        setRelatorio(
          buildRelatorioFromRegistros([], {
            granularidade: granularidade?.value || "diario",
            excluirInvalidos,
            periodo: { ini: startDate, fim: endDate },
          }),
        )
        Swal.fire({
          icon: "warning",
          text: "Nenhum registro encontrado no período.",
        })
        return
      }

      const built = buildRelatorioFromRegistros(allRows, {
        granularidade: granularidade?.value || "diario",
        excluirInvalidos,
        periodo: { ini: startDate, fim: endDate },
      })
      setRelatorio(built)
    } catch (err) {
      console.warn("[MoradorDashboard] falha ao buscar consumo", err)
      Swal.fire({
        icon: "warning",
        text: "Não foi possível carregar o consumo do período.",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <div className="page-title-box">
          <Row className="align-items-center">
            <Col md={8}>
              <label id="component-title">Minha unidade</label>
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item active">
                  Acompanhe a leitura e o consumo do seu imóvel.
                </li>
              </ol>
            </Col>
          </Row>
        </div>

        <Row className="mb-3">
          <Col lg={4}>
            <Card>
              <CardBody>
                <h5 className="card-title mb-3">Unidade</h5>
                <p className="mb-1">{unitLabel}</p>
                <p className="text-muted mb-0">
                  {meters.length
                    ? `${meters.length} medidor(es) vinculado(s)`
                    : "Nenhum medidor vinculado"}
                </p>
              </CardBody>
            </Card>
          </Col>
          <Col lg={4}>
            <Card>
              <CardBody>
                <h5 className="card-title mb-3">Leitura atual</h5>
                <p className="display-6 mb-0">
                  {relatorio?.latestReading != null
                    ? Number(relatorio.latestReading).toLocaleString("pt-BR")
                    : "—"}
                </p>
              </CardBody>
            </Card>
          </Col>
          <Col lg={4}>
            <Card>
              <CardBody>
                <h5 className="card-title mb-3">Consumo no período</h5>
                <p className="display-6 mb-0">
                  {relatorio != null
                    ? `${formatNumber(relatorio.total_m3)} m³`
                    : "—"}
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="mb-4">
                  <h5 className="card-title mb-1">Consumo da unidade</h5>
                  <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                    Total do período no topo. Escolha linhas por dia ou por
                    pulso. Somente os medidores da sua unidade.
                  </p>
                </div>

                <Row className="g-3 mb-2">
                  <Col md={3} lg={2}>
                    <label className="form-label" htmlFor="morador-dt-ini">
                      Data início
                    </label>
                    <input
                      id="morador-dt-ini"
                      type="date"
                      className="form-control"
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                      disabled={isLoading}
                    />
                  </Col>
                  <Col md={3} lg={2}>
                    <label className="form-label" htmlFor="morador-dt-fim">
                      Data fim
                    </label>
                    <input
                      id="morador-dt-fim"
                      type="date"
                      className="form-control"
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      disabled={isLoading}
                    />
                  </Col>
                  <Col md={6} lg={3}>
                    <label className="form-label">Linhas</label>
                    <Select
                      options={GRANULARIDADE_OPTIONS}
                      value={granularidade}
                      onChange={opt =>
                        setGranularidade(opt || GRANULARIDADE_OPTIONS[0])
                      }
                      isSearchable={false}
                      isDisabled={isLoading}
                    />
                  </Col>
                  <Col md={12} lg={5} className="d-flex align-items-end">
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="morador-excluir-invalidos"
                        checked={excluirInvalidos}
                        onChange={() => setExcluirInvalidos(v => !v)}
                        disabled={isLoading}
                      />
                      <label
                        className="form-check-label ms-1"
                        htmlFor="morador-excluir-invalidos"
                      >
                        Excluir pulsos inválidos
                        <span
                          className="d-block text-muted"
                          style={{ fontSize: 12, fontWeight: 400 }}
                        >
                          Zera rollbacks (recomendado)
                        </span>
                      </label>
                    </div>
                  </Col>
                </Row>

                <Row className="g-3 align-items-center mb-3">
                  <Col className="d-flex gap-2 flex-wrap">
                    <Button
                      color="primary"
                      onClick={gerarRelatorio}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Spinner size="sm" className="me-1" /> Gerando...
                        </>
                      ) : (
                        "Gerar"
                      )}
                    </Button>
                  </Col>
                </Row>

                {meters.length > 0 ? (
                  <div className="table-responsive mb-4">
                    <Table className="table-nowrap mb-0">
                      <thead>
                        <tr>
                          <th>Medidor</th>
                          <th>Unidade</th>
                        </tr>
                      </thead>
                      <tbody>
                        {meters.map(m => (
                          <tr key={m.OID_MEDIDOR}>
                            <td>{m.NR_MEDIDOR || m.OID_MEDIDOR}</td>
                            <td>{m.imovel || unitLabel}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : null}

                {relatorio ? (
                  <>
                    <div
                      className="d-flex flex-wrap gap-4 mb-3 p-3 rounded"
                      style={{
                        background: "#f8f9fb",
                        border: "1px solid #eef0f4",
                      }}
                    >
                      <div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Unidade
                        </div>
                        <strong>{unitLabel}</strong>
                      </div>
                      <div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Período
                        </div>
                        <strong>
                          {formatDateBr(relatorio.periodo?.ini)} —{" "}
                          {formatDateBr(relatorio.periodo?.fim)}
                        </strong>
                      </div>
                      <div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Modo
                        </div>
                        <strong>{isPulso ? "Por pulso" : "Por dia"}</strong>
                      </div>
                      <div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Medidores
                        </div>
                        <strong>{relatorio.qtd_medidores}</strong>
                      </div>
                      <div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Linhas
                        </div>
                        <strong>
                          {relatorio.qtd_linhas ?? relatorio.itens?.length ?? 0}
                        </strong>
                      </div>
                      <div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Total litros
                        </div>
                        <strong style={{ color: "#556ee6", fontSize: 18 }}>
                          {formatNumber(relatorio.total_litros, 0)} L
                        </strong>
                      </div>
                      <div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          Total m³
                        </div>
                        <strong style={{ color: "#556ee6", fontSize: 18 }}>
                          {formatNumber(relatorio.total_m3)} m³
                        </strong>
                      </div>
                    </div>

                    <div
                      className="table-responsive"
                      style={{ maxHeight: 520 }}
                    >
                      <Table className="table table-sm table-hover mb-0">
                        <thead
                          style={{
                            position: "sticky",
                            top: 0,
                            background: "#fff",
                            zIndex: 1,
                          }}
                        >
                          {isPulso ? (
                            <tr>
                              <th>Data/Hora</th>
                              <th>Imóvel</th>
                              <th>Hidrômetro</th>
                              <th className="text-end">Pulso</th>
                              <th className="text-end">Leitura</th>
                              <th className="text-end">Litros</th>
                              <th className="text-end">m³</th>
                            </tr>
                          ) : (
                            <tr>
                              <th>Data</th>
                              <th>Imóvel</th>
                              <th>Hidrômetro</th>
                              <th className="text-end">Leit. ini</th>
                              <th className="text-end">Leit. fim</th>
                              <th className="text-end">Qtd</th>
                              <th className="text-end">Litros</th>
                              <th className="text-end">m³</th>
                            </tr>
                          )}
                        </thead>
                        <tbody>
                          {isPulso
                            ? relatorio.itens.map((item, idx) => (
                                <tr
                                  key={`${item.OID_MEDIDOR}-${item.DT_LEITURA}-${idx}`}
                                >
                                  <td>{formatDateTimeBr(item.DT_LEITURA)}</td>
                                  <td>{item.DS_COMPLEMENTO || "—"}</td>
                                  <td>{item.NR_HIDROMETRO}</td>
                                  <td className="text-end">{item.NR_PULSO}</td>
                                  <td className="text-end">
                                    {item.LEITURA_ATUAL}
                                  </td>
                                  <td className="text-end fw-semibold">
                                    {formatNumber(item.CONSUMO_LITROS, 0)}
                                  </td>
                                  <td className="text-end fw-semibold">
                                    {formatNumber(item.CONSUMO_M3)}
                                  </td>
                                </tr>
                              ))
                            : relatorio.itens.map((item, idx) => (
                                <tr
                                  key={`${item.OID_MEDIDOR}-${item.DT_DIA}-${idx}`}
                                >
                                  <td>
                                    {formatDateBr(
                                      item.DT_DIA || item.DT_PRIMEIRA,
                                    )}
                                  </td>
                                  <td>{item.DS_COMPLEMENTO || "—"}</td>
                                  <td>{item.NR_HIDROMETRO}</td>
                                  <td className="text-end">
                                    {item.LEITURA_INI}
                                  </td>
                                  <td className="text-end">
                                    {item.LEITURA_FIM}
                                  </td>
                                  <td className="text-end">
                                    {item.QTD_LEITURAS}
                                  </td>
                                  <td className="text-end fw-semibold">
                                    {formatNumber(item.CONSUMO_LITROS, 0)}
                                  </td>
                                  <td className="text-end fw-semibold">
                                    {formatNumber(item.CONSUMO_M3)}
                                  </td>
                                </tr>
                              ))}
                        </tbody>
                      </Table>
                    </div>
                  </>
                ) : (
                  !isLoading && (
                    <div
                      className="text-center text-muted py-5"
                      style={{
                        border: "1px dashed #dde1ea",
                        borderRadius: 8,
                      }}
                    >
                      Configure o período e clique em Gerar para ver o consumo
                      da sua unidade.
                    </div>
                  )
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default MoradorDashboard
